import { useState, type FormEvent } from "react";
import { Lock, Eye, EyeOff, KeyRound, ShieldAlert, ArrowRight, Check } from "lucide-react";
import { ApiError, changePassword, type CurrentUser } from "../lib/api";

type ChangePasswordPageProps = {
  token: string;
  user: CurrentUser;
  onChanged: () => void;
};

export function ChangePasswordPage({ token, user, onChanged }: ChangePasswordPageProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const hasMinLength = newPassword.length >= 8;
  const hasLower = /[a-z]/.test(newPassword);
  const hasUpper = /[A-Z]/.test(newPassword);
  const hasNumber = /[0-9]/.test(newPassword);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (newPassword !== confirmation) {
      setError("New password confirmation does not match");
      return;
    }
    if (!hasMinLength || !hasLower || !hasUpper || !hasNumber) {
      setError("New password does not meet the security requirements");
      return;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      await changePassword(token, currentPassword, newPassword);
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Unable to change password");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4 selection:bg-emerald-500 selection:text-white">
      <main className="w-full max-w-md">
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm relative">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center font-bold text-white text-xl mx-auto mb-3 shadow-sm">
              <KeyRound className="w-6 h-6" />
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold mb-2">
              <ShieldAlert className="w-3.5 h-3.5 text-amber-600" />
              <span>Mandatory Security Update</span>
            </div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">Set Permanent Password</h1>
            <p className="text-slate-500 text-xs mt-1">
              Signed in as <strong className="text-slate-800 font-semibold">{user.username}</strong>. Please replace your temporary initial password.
            </p>
          </div>

          <form onSubmit={(event) => void submit(event)} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Current Temporary Password
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type={showCurrent ? "text" : "password"}
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  required
                  className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-slate-300 text-slate-900 text-xs focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 bg-white placeholder:text-slate-400 shadow-2xs"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                  aria-label="Toggle password visibility"
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="text-xs font-semibold text-slate-700">New Password</label>
                <span className="text-[11px] text-slate-400 font-normal">Min 8 chars</span>
              </div>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type={showNew ? "text" : "password"}
                  autoComplete="new-password"
                  minLength={8}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  required
                  className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-slate-300 text-slate-900 text-xs focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 bg-white placeholder:text-slate-400 shadow-2xs"
                  placeholder="Create strong password"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                  aria-label="Toggle password visibility"
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {/* Password Requirement Indicators */}
              <div className="mt-2 grid grid-cols-2 gap-1.5 text-[11px] p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                <span className={`flex items-center gap-1.5 ${hasMinLength ? "text-emerald-700 font-semibold" : "text-slate-400"}`}>
                  <Check className={`w-3 h-3 ${hasMinLength ? "text-emerald-600" : "text-slate-300"}`} />
                  8+ characters
                </span>
                <span className={`flex items-center gap-1.5 ${hasLower ? "text-emerald-700 font-semibold" : "text-slate-400"}`}>
                  <Check className={`w-3 h-3 ${hasLower ? "text-emerald-600" : "text-slate-300"}`} />
                  Lowercase letter
                </span>
                <span className={`flex items-center gap-1.5 ${hasUpper ? "text-emerald-700 font-semibold" : "text-slate-400"}`}>
                  <Check className={`w-3 h-3 ${hasUpper ? "text-emerald-600" : "text-slate-300"}`} />
                  Uppercase letter
                </span>
                <span className={`flex items-center gap-1.5 ${hasNumber ? "text-emerald-700 font-semibold" : "text-slate-400"}`}>
                  <Check className={`w-3 h-3 ${hasNumber ? "text-emerald-600" : "text-slate-300"}`} />
                  Number (0-9)
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Confirm New Password
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type={showConfirm ? "text" : "password"}
                  autoComplete="new-password"
                  minLength={8}
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  required
                  className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-slate-300 text-slate-900 text-xs focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 bg-white placeholder:text-slate-400 shadow-2xs"
                  placeholder="Repeat new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                  aria-label="Toggle password visibility"
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {confirmation && confirmation !== newPassword && (
                <p className="text-[11px] text-rose-500 mt-1">Passwords do not match</p>
              )}
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full py-2.5 text-xs font-bold flex items-center justify-center gap-2 mt-2 shadow-sm"
            >
              <span>{submitting ? "Updating Password..." : "Update Password & Continue"}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <div className="text-center mt-5 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onChanged}
              className="text-xs text-slate-500 hover:text-slate-800 transition-colors font-medium"
            >
              Cancel and Return to Sign In
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
